import {Gauge} from '@dropbox/dig-components/gauge';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, CloseLine} from '@dropbox/dig-icons/assets';
import type {WorkstreamAuditOnTimeUpdates, WorkstreamAuditUpdateCycle} from 'client';
import {formatShortDate} from 'helpers/dates';
import {t} from 'i18next';

import styles from './WorkstreamAuditOnTimeUpdatesGauge.module.css';

export const WORKSTREAM_AUDIT_ON_TIME_UPDATES_TEST_ID = 'workstream-audit-on-time-updates';
export const WORKSTREAM_AUDIT_ON_TIME_UPDATES_CONTAINER_TEST_ID =
  'workstream-audit-on-time-updates-container';
export const WORKSTREAM_AUDIT_UPDATE_TOOLTIP_TRIGGER_TEST_ID =
  'workstream-audit-update-tooltip-trigger';
export const WORKSTREAM_AUDIT_UPDATE_TOOLTIP_LIST_TEST_ID = 'workstream-audit-update-tooltip-list';

const getCycleDateLabel = (value: string): string => formatShortDate(value) || value;

const WorkstreamAuditUpdateCycleTooltipRow = ({cycle}: {cycle: WorkstreamAuditUpdateCycle}) => {
  const dueDate = getCycleDateLabel(cycle.due_date);
  const statusLabel = cycle.submitted
    ? t('workstream_audit_update_cycle_submitted_status')
    : t('workstream_audit_update_cycle_missed_status');

  return (
    <Split gap="4" alignY="center">
      <Split.Item className={styles.tooltipStatusIcon}>
        <UIIcon
          src={cycle.submitted ? CheckmarkLine : CloseLine}
          size="small"
          isOpticallyAligned
          role="img"
          aria-label={statusLabel}
          data-testid={`workstream-audit-update-cycle-${
            cycle.submitted ? 'submitted' : 'missed'
          }-icon`}
        />
      </Split.Item>
      <Split.Item>
        <Text size="small">{t('workstream_audit_update_cycle_due_date', {dueDate})}</Text>
      </Split.Item>
    </Split>
  );
};

export const WorkstreamAuditOnTimeUpdatesGauge = ({
  onTimeUpdates,
}: {
  onTimeUpdates?: WorkstreamAuditOnTimeUpdates | null;
}) => {
  if (!onTimeUpdates || onTimeUpdates.cycle_count <= 0) {
    return <Text color="subtle">{t('workstream_audit_missing_value')}</Text>;
  }

  const {submitted_count: submittedCount, cycle_count: cycleCount} = onTimeUpdates;
  const value = (submittedCount / cycleCount) * 100;
  const label = t('workstream_audit_on_time_updates').toString();
  const valueText = t('workstream_audit_on_time_updates_value', {
    submittedCount,
    cycleCount,
  }).toString();
  const tooltipTitle = t('workstream_audit_on_time_updates_tooltip_title', {
    submittedCount,
    cycleCount,
  }).toString();
  const percentageText = `${Math.round(value)}%`;
  const cycles = onTimeUpdates.cycles ?? [];

  const gauge = (
    <Gauge
      value={value}
      size="xsmall"
      shouldAnimate={false}
      aria-label={label}
      aria-valuetext={valueText}
      className={styles.gauge}
      data-testid={WORKSTREAM_AUDIT_ON_TIME_UPDATES_TEST_ID}
      tabIndex={0}
    >
      <Gauge.Content className={styles.content}>
        <Gauge.Title hasTooltip={false} className={styles.title}>
          {percentageText}
        </Gauge.Title>
      </Gauge.Content>
    </Gauge>
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      data-testid={WORKSTREAM_AUDIT_ON_TIME_UPDATES_CONTAINER_TEST_ID}
    >
      {cycles.length > 0 ? (
        <Tooltip
          title={
            <Stack gap="8">
              <Text size="small" isBold>
                {tooltipTitle}
              </Text>
              <div
                className={styles.tooltipCycleList}
                data-testid={WORKSTREAM_AUDIT_UPDATE_TOOLTIP_LIST_TEST_ID}
              >
                <Stack gap="4">
                  {cycles.map((cycle, index) => (
                    <WorkstreamAuditUpdateCycleTooltipRow
                      key={`${cycle.due_date}-${index}`}
                      cycle={cycle}
                    />
                  ))}
                </Stack>
              </div>
            </Stack>
          }
          placement="bottom"
          auto
        >
          <span
            className={styles.tooltipTrigger}
            data-testid={WORKSTREAM_AUDIT_UPDATE_TOOLTIP_TRIGGER_TEST_ID}
          >
            {gauge}
          </span>
        </Tooltip>
      ) : (
        gauge
      )}
    </Box>
  );
};
